import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useSessionRealtime } from '../../hooks/useSessionRealtime';
import { api } from '../../utils/api';
import TicketPicker from '../jira/TicketPicker';
import BaselinePanel from './BaselinePanel';
import {
  Users, Plus, ExternalLink, Eye, UserX, CheckCircle,
  RotateCcw, Bookmark, AlertTriangle, Volume2, VolumeX,
  Share2, ChevronDown, ChevronUp, Zap, Copy, XCircle,
  SkipForward, List, History, Link2
} from 'lucide-react';

const CARD_VALUES = ['1', '2', '3', '5', '8', '∞', '☕'];

const REACTIONS = [
  { emoji: '👍', label: 'Agree' },
  { emoji: '🤔', label: 'Unsure' },
  { emoji: '😮', label: 'Surprised' },
  { emoji: '🎯', label: 'Spot on' },
];

function playSound(type) {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.connect(gain);
    gain.connect(ctx.destination);
    if (type === 'vote') { osc.frequency.value = 440; gain.gain.value = 0.1; }
    if (type === 'reveal') { osc.frequency.value = 880; gain.gain.value = 0.15; }
    if (type === 'consensus') { osc.frequency.value = 1046; gain.gain.value = 0.2; }
    osc.start();
    gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.3);
    osc.stop(ctx.currentTime + 0.3);
  } catch { /* audio not supported */ }
}


// ─── Estimation Guidance Panel ──────────────────────────────────────────────
const HINTS = [
  { icon: '🎯', text: 'Estimate complexity, not time. A 5-point story takes the same points whether it takes one developer a day or two days.' },
  { icon: '📐', text: 'Use your baseline stories. "Is this simpler or more complex than our 3-point reference?" beats debating from scratch.' },
  { icon: '🧪', text: 'Story points include QA effort, not just development. If testing is complex, the number goes up.' },
  { icon: '🔗', text: 'Dependencies add points. If this story needs another team, an undocumented API, or a third-party service, add uncertainty.' },
  { icon: '📋', text: 'Wide disagreement usually means the story is not ready. Use ☕ to flag it for more discussion before estimating.' },
  { icon: '♾️', text: 'If you cannot estimate it without breaking it down first, vote ∞. The PM and Tech Lead will split the story.' },
  { icon: '🚫', text: 'Never estimate in hours. "This will take 4 hours" leads to micro-management and wrong expectations.' },
  { icon: '👥', text: 'Do not factor in who will do the work. A story is a 3 whether a senior or junior developer picks it up.' },
  { icon: '🔄', text: 'Documentation and code review are part of the story. They are already baked into your point scale.' },
  { icon: '⚠️', text: 'High unknowns = higher points. If requirements are vague or the technical approach is unclear, go conservative.' },
  { icon: '📊', text: 'The first person to speak anchors the group. That is why we reveal simultaneously — vote before you discuss.' },
  { icon: '✂️', text: 'Maximum story size is 8 points. If you want to vote higher, the story needs to be split first.' },
];

const SCALE = [
  { value: '1', label: 'Trivial', desc: 'Text change, label update, single config field. No logic, no risk.' },
  { value: '2', label: 'Simple', desc: 'Straightforward CRUD, minor UI change. Clear approach, no surprises.' },
  { value: '3', label: 'Moderate', desc: 'Small feature with testing, minor API call. Understood, low risk.' },
  { value: '5', label: 'Complex', desc: 'Multiple components, integration work. Some unknowns but manageable.' },
  { value: '8', label: 'Large', desc: 'Cross-system, high complexity or high uncertainty. Consider splitting.' },
  { value: '∞', label: 'Too big', desc: 'Cannot estimate without breaking down. Vote this to flag for splitting.' },
  { value: '☕', label: 'Needs talk', desc: 'Requirements unclear. Needs more discussion before the team can estimate.' },
];

function EstimationGuidancePanel({ roundNumber, isRevealed }) {
  const storageKey = 'pp_guidance_expanded';
  const [expanded, setExpanded] = React.useState(() => {
    try { return localStorage.getItem(storageKey) !== 'false'; } catch { return true; }
  });
  const [activeScale, setActiveScale] = React.useState(null);
  const hint = HINTS[(roundNumber - 1) % HINTS.length];
  const toggle = () => {
    const next = !expanded;
    setExpanded(next);
    try { localStorage.setItem(storageKey, String(next)); } catch {}
  };
  if (isRevealed) return null;
  return (
    <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', marginBottom: 20, overflow: 'hidden' }}>
      {/* Always-visible tier */}
      <div style={{ padding: '12px 16px' }}>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 10 }}>
          {SCALE.map(s => (
            <button key={s.value} onClick={() => setActiveScale(activeScale === s.value ? null : s.value)} style={{
              background: activeScale === s.value ? 'var(--accent-dim)' : 'var(--bg-elevated)',
              border: `1px solid ${activeScale === s.value ? 'var(--accent)' : 'var(--border)'}`,
              borderRadius: 6, padding: '3px 10px',
              fontFamily: 'var(--font-mono, DM Mono, monospace)', fontWeight: 700, fontSize: 13,
              color: activeScale === s.value ? 'var(--accent)' : 'var(--text-primary)',
              cursor: 'pointer', transition: 'all 0.15s',
            }}>{s.value}</button>
          ))}
        </div>
        {activeScale && (() => {
          const s = SCALE.find(x => x.value === activeScale);
          return s ? (
            <div style={{ background: 'var(--accent-dim)', border: '1px solid var(--accent)', borderRadius: 6, padding: '8px 12px', marginBottom: 10, fontSize: 13 }}>
              <span style={{ color: 'var(--accent)', fontWeight: 700 }}>{s.value} — {s.label}:</span>{' '}
              <span style={{ color: 'var(--text-primary)' }}>{s.desc}</span>
            </div>
          ) : null;
        })()}
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, background: 'var(--bg-elevated)', borderRadius: 6, padding: '8px 12px' }}>
          <span style={{ fontSize: 16, flexShrink: 0, marginTop: 1 }}>{hint.icon}</span>
          <p style={{ fontSize: 13, color: 'var(--text-primary)', lineHeight: 1.6, margin: 0 }}>{hint.text}</p>
          <button onClick={toggle} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', fontSize: 11, flexShrink: 0, padding: '2px 4px', alignSelf: 'center', whiteSpace: 'nowrap' }}>
            {expanded ? 'Less ▲' : 'More ▼'}
          </button>
        </div>
      </div>
      {/* Collapsible deep-dive tier */}
      {expanded && (
        <div style={{ borderTop: '1px solid var(--border)', padding: '16px' }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16 }}>
            <div>
              <p style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.07em', color: 'var(--accent)', marginBottom: 8 }}>What to consider</p>
              {[
                ['Complexity', 'How many systems, components, or files are touched?'],
                ['Effort', 'Development + QA testing + documentation + code review.'],
                ['Unknowns', 'Is the technical approach clear? Are requirements finalised?'],
                ['Dependencies', 'Does this rely on another team, API, or external service?'],
                ['History', 'Has the team built something similar before?'],
              ].map(([label, detail]) => (
                <div key={label} style={{ marginBottom: 8 }}>
                  <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-primary)' }}>{label} </span>
                  <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{detail}</span>
                </div>
              ))}
            </div>
            <div>
              <p style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.07em', color: 'var(--green)', marginBottom: 8 }}>The Process</p>
              {[
                ['1', 'PM presents the story'],
                ['2', 'Team asks questions — devs, QA, and Tech Leads all contribute. Domain expert gives context but does not anchor a number.'],
                ['3', 'Round 1 vote — everyone submits simultaneously'],
                ['4', 'If no consensus, discuss the gap then vote again (Round 2)'],
                ['5', 'If still no alignment, discuss further to reach agreement'],
                ['6', 'Last resort: go with the conservative number if at least 40% agree'],
              ].map(([step, text]) => (
                <div key={step} style={{ display: 'flex', gap: 8, marginBottom: 8, alignItems: 'flex-start' }}>
                  <span style={{ fontSize: 11, fontWeight: 700, fontFamily: 'monospace', background: 'var(--bg-elevated)', border: '1px solid var(--border)', borderRadius: 4, padding: '1px 6px', flexShrink: 0, color: 'var(--accent)' }}>{step}</span>
                  <span style={{ fontSize: 12, color: 'var(--text-secondary)', lineHeight: 1.5 }}>{text}</span>
                </div>
              ))}
            </div>
            <div>
              <p style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.07em', color: 'var(--red)', marginBottom: 8 }}>Do not do this</p>
              {[
                ['❌', 'Estimate in hours or days — story points only'],
                ['❌', 'Average the votes instead of discussing the gap'],
                ['❌', 'Let the Tech Lead decide alone — everyone votes'],
                ['❌', 'Factor in who will do the work'],
                ['❌', 'Skip ☕ when requirements are genuinely unclear'],
                ['❌', 'Vote higher than 8 — split the story instead'],
              ].map(([icon, text]) => (
                <div key={text} style={{ display: 'flex', gap: 8, marginBottom: 8, fontSize: 12, color: 'var(--text-secondary)', alignItems: 'flex-start' }}>
                  <span style={{ flexShrink: 0 }}>{icon}</span>
                  <span>{text}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default function SessionPage() {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [session, setSession] = useState(null);
  const [participants, setParticipants] = useState([]);
  const [rounds, setRounds] = useState([]);
  const [currentRound, setCurrentRound] = useState(null);
  const [votes, setVotes] = useState([]);
  const [allRoundVotes, setAllRoundVotes] = useState([]); // all vote_numbers for history
  const [myVote, setMyVote] = useState(null);
  const [myReaction, setMyReaction] = useState(null);
  const [reactions, setReactions] = useState([]); // ephemeral broadcast reactions
  // Latest emoji reaction per participant (for panel display)
  const latestReactionByParticipant = React.useMemo(() => {
    const map = {};
    reactions.forEach(r => { map[r.participantId] = r.emoji; });
    return map;
  }, [reactions]);
  const [myParticipantId, setMyParticipantId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Queue state
  const [ticketQueue, setTicketQueue] = useState([]); // array of ticket objects
  const [queueIndex, setQueueIndex] = useState(0);

  // UI state
  const [showTicketPicker, setShowTicketPicker] = useState(false);
  const [showBaselines, setShowBaselines] = useState(false);
  const [showParticipants, setShowParticipants] = useState(true);
  const [showVoteHistory, setShowVoteHistory] = useState(false);
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [copied, setCopied] = useState(false);
  const [copiedObserver, setCopiedObserver] = useState(false);

  // Complete modal state
  const [showCompleteModal, setShowCompleteModal] = useState(false);
  const [finalPoints, setFinalPoints] = useState('');
  const [isUncertain, setIsUncertain] = useState(false);
  const [isBaseline, setIsBaseline] = useState(false);
  const [baselineNotes, setBaselineNotes] = useState('');
  const [comment, setComment] = useState('');
  const [savingToJira, setSavingToJira] = useState(false);

  const isFacilitator = user && session?.facilitator_jira_id === user.jiraId;

  const loadSession = useCallback(async () => {
    try {
      const data = await api.sessions.get(sessionId);
      setSession(data);
      setParticipants(data.participants || []);
      setRounds(data.rounds || []);

      const active = (data.rounds || []).findLast(r => r.status !== 'complete' && r.status !== 'skipped');
      // BUG-03: don't let a stale realtime reload downgrade a locally-revealed round back to 'voting'
      setCurrentRound(prev => {
        if (!active) return null;
        if (prev?.id === active.id && prev?.status === 'revealed' && active.status === 'voting') {
          return prev;
        }
        return active;
      });

      const storedPid = localStorage.getItem(`pp_participant_${sessionId}`);
      if (!storedPid && user) {
        const myP = (data.participants || []).find(p => p.jira_id === user.jiraId);
        if (myP) {
          localStorage.setItem(`pp_participant_${sessionId}`, myP.id);
          setMyParticipantId(myP.id);
        }
      }
    } catch (e) {
      setError('Session not found.');
    } finally {
      setLoading(false);
    }
  }, [sessionId, user]);

  const loadVotes = useCallback(async () => {
    if (!currentRound) return;
    try {
      const v = await api.sessions.votes(sessionId, currentRound.id);
      setVotes(v);
      // Load all votes across all round numbers for history view
      const all = await api.sessions.allVotes(sessionId, currentRound.id);
      setAllRoundVotes(all);

      const pid = localStorage.getItem(`pp_participant_${sessionId}`);
      if (pid) {
        const mine = v.find(v => v.participant_id === pid && v.vote_number === currentRound.round_number);
        setMyVote(mine?.vote_value || null);
      }
    } catch { /* ignore */ }
  }, [sessionId, currentRound]);

  useEffect(() => {
    const pid = localStorage.getItem(`pp_participant_${sessionId}`);
    setMyParticipantId(pid);
    if (!pid && !user) navigate(`/join/${sessionId}`);
    loadSession();
  }, [sessionId]);

  useEffect(() => { loadVotes(); }, [currentRound?.id, currentRound?.round_number]);

  // BUG-05: handle incoming broadcast reactions
  const handleReaction = useCallback(({ participantId, name, emoji }) => {
    const id = Date.now() + Math.random();
    setReactions(rs => [...rs, { id, participantId, name, emoji }]);
    setTimeout(() => setReactions(rs => rs.filter(r => r.id !== id)), 3000);
  }, []);

  const { sendReaction } = useSessionRealtime(sessionId, {
    onParticipants: loadSession,
    onRounds: loadSession,
    onVotes: () => {
      loadVotes();
      if (soundEnabled) playSound('vote');
    },
    onReaction: handleReaction,
    onSession: loadSession,
  });

  // BUG-05: broadcast reaction to all participants, then update local state
  const handleReact = useCallback((emoji) => {
    setMyReaction(emoji);
    if (emoji && myParticipantId) {
      const myP = participants.find(p => p.id === myParticipantId);
      sendReaction({ participantId: myParticipantId, name: myP?.display_name || 'Someone', emoji });
    }
  }, [myParticipantId, participants, sendReaction]);

  // Detect kick
  useEffect(() => {
    if (!myParticipantId || participants.length === 0) return;
    const stillHere = participants.find(p => p.id === myParticipantId);
    if (!stillHere) {
      setError('You have been removed from this session.');
      localStorage.removeItem(`pp_participant_${sessionId}`);
      setTimeout(() => navigate('/'), 3000);
    }
  }, [participants, myParticipantId]);

  const voters = participants.filter(p => !p.is_observer && p.is_active);
  const votesThisRound = votes.filter(v => v.vote_number === (currentRound?.round_number || 1));
  const voterIds = new Set(voters.map(p => p.id));
  const votedIds = new Set(votesThisRound.map(v => v.participant_id));
  const allVoted = voterIds.size > 0 && [...voterIds].every(id => votedIds.has(id));

  useEffect(() => {
    if (allVoted && currentRound?.status === 'voting' && soundEnabled) playSound('reveal');
  }, [allVoted]);

  // ── Actions ──────────────────────────────────────────────────

  const startRoundForTicket = async (ticket) => {
    try {
      const round = await api.sessions.createRound(sessionId, {
        jira_ticket_key: ticket.key,
        jira_ticket_summary: ticket.summary,
        jira_ticket_url: ticket.url,
        jira_ticket_description: JSON.stringify(ticket.description || ''),
        jira_ticket_type: ticket.type,
        jira_ticket_priority: ticket.priority
      });
      setCurrentRound(round);
      setMyVote(null);
      setMyReaction(null);
      setVotes([]);
      setAllRoundVotes([]);
      setShowVoteHistory(false);
    } catch (e) { setError(e.message); }
  };

  const handleSelectTicket = async (ticket) => {
    setShowTicketPicker(false);
    // BUG-06: if a queue is active, offer to append rather than replace
    if (queueActive && currentRound) {
      if (!window.confirm(`Add ${ticket.key} to the end of the queue? (${queueRemaining} ticket${queueRemaining !== 1 ? 's' : ''} already queued after this one)`)) return;
      setTicketQueue(prev => [...prev, ticket]);
      return;
    }
    await startRoundForTicket(ticket);
  };

  const handleQueueTickets = (tickets) => {
    setShowTicketPicker(false);
    // BUG-06: if a queue is active, offer to append rather than replace
    if (queueActive && currentRound) {
      if (!window.confirm(`Add ${tickets.length} ticket${tickets.length !== 1 ? 's' : ''} to the end of the current queue?`)) return;
      setTicketQueue(prev => [...prev, ...tickets]);
      return;
    }
    setTicketQueue(tickets);
    setQueueIndex(0);
    startRoundForTicket(tickets[0]);
  };

  const handleNextInQueue = async () => {
    const nextIdx = queueIndex + 1;
    if (nextIdx >= ticketQueue.length) {
      setTicketQueue([]);
      setQueueIndex(0);
      return;
    }
    setQueueIndex(nextIdx);
    await startRoundForTicket(ticketQueue[nextIdx]);
  };

  const handleVote = async (value) => {
    if (!myParticipantId) return;
    const voteNum = currentRound?.round_number || 1;
    try {
      const vote = await api.sessions.vote(sessionId, currentRound.id, {
        participant_id: myParticipantId,
        vote_value: value,
        vote_number: voteNum
      });
      setMyVote(value);
      setVotes(vs => {
        const without = vs.filter(v => !(v.participant_id === myParticipantId && v.vote_number === voteNum));
        return [...without, vote];
      });
    } catch (e) { setError(e.message); }
  };

  const handleReveal = async () => {
    try {
      const updated = await api.sessions.reveal(sessionId, currentRound.id);
      setCurrentRound(updated);
    } catch (e) { setError(e.message); }
  };

  const handleRevote = async () => {
    try {
      const updated = await api.sessions.revote(sessionId, currentRound.id);
      setCurrentRound(updated);
      setMyVote(null);
      setMyReaction(null);
      setVotes([]);
    } catch (e) { setError(e.message); }
  };

  const handleSkip = async () => {
    if (!window.confirm(`Skip ${currentRound.jira_ticket_key}? No points will be saved to Jira.`)) return;
    try {
      await api.sessions.skipRound(sessionId, currentRound.id);
      setCurrentRound(null);
      setMyVote(null);
      setMyReaction(null);
      setVotes([]);
      // Auto-advance queue if active
      if (ticketQueue.length > 0) {
        await handleNextInQueue();
      } else {
        await loadSession();
      }
    } catch (e) { setError(e.message); }
  };

  // FEAT-03: Queue management handlers
  const handleMoveInQueue = useCallback((index, direction) => {
    const newIndex = index + direction;
    if (index <= queueIndex || newIndex <= queueIndex || newIndex >= ticketQueue.length) return;
    setTicketQueue(prev => {
      const next = [...prev];
      [next[index], next[newIndex]] = [next[newIndex], next[index]];
      return next;
    });
  }, [queueIndex, ticketQueue.length]);

  const handleRemoveFromQueue = useCallback((index) => {
    setTicketQueue(prev => prev.filter((_, i) => i !== index));
    if (index < queueIndex) setQueueIndex(q => q - 1);
  }, [queueIndex]);

  const handleJumpToQueueItem = useCallback(async (index) => {
    if (index === queueIndex) return;
    const targetTicket = ticketQueue[index];
    const currentTicket = ticketQueue[queueIndex];
    if (currentRound && ['voting', 'revoting', 'revealed'].includes(currentRound.status)) {
      const ok = window.confirm(`Skip ${currentTicket?.jira_ticket_key || 'current ticket'} and jump to ${targetTicket?.jira_ticket_key || 'selected ticket'}?`);
      if (!ok) return;
      try { await api.rounds.skip(sessionId, currentRound.id); } catch { /* best effort */ }
    }
    setQueueIndex(index);
    try { await startRoundForTicket(targetTicket); } catch (e) { setError(e.message); }
  }, [queueIndex, ticketQueue, currentRound, sessionId]);

  const handleComplete = async () => {
    setSavingToJira(true);
    let jiraError = null;

    try {
      // BUG-04: try Jira write but don't abort if it fails — always save locally
      const numericPoints = parseInt(finalPoints);
      const isNumeric = !isNaN(numericPoints);
      if (isNumeric) {
        try {
          await api.jira.updatePoints(currentRound.jira_ticket_key, {
            points: numericPoints,
            uncertain: isUncertain,
            addReestimateLabel: isUncertain
          });
        } catch (e) {
          jiraError = e.message;
        }
      }
      // Always save the estimation to the local DB regardless of Jira outcome
      await api.sessions.complete(sessionId, currentRound.id, {
        final_points: isNumeric ? numericPoints : null,
        is_uncertain: isUncertain,
        is_baseline: isBaseline,
        baseline_notes: baselineNotes
      });

      // FEAT-06: post comment to Jira (non-blocking)
      if (comment.trim()) {
        try {
          await api.jira.addComment(currentRound.jira_ticket_key, {
            sessionName: session.name,
            date: new Date().toLocaleDateString(),
            points: finalPoints,
            comment: comment.trim(),
            participantNames: participants.filter(p => p.is_active).map(p => p.display_name)
          });
        } catch (e) {
          console.warn('[FEAT-06] Failed to post Jira comment:', e.message);
        }
      }

      setShowCompleteModal(false);
      setFinalPoints(''); setIsUncertain(false); setIsBaseline(false); setBaselineNotes('');
      setComment('');
      setCurrentRound(null); setMyVote(null); setMyReaction(null); setVotes([]); setAllRoundVotes([]);

      if (jiraError) {
        const isExpired = jiraError.includes('expired') || jiraError.includes('sign in');
        setError(isExpired
          ? `Jira session expired — ${currentRound.jira_ticket_key} was NOT updated in Jira. Please sign in again and manually set it to ${finalPoints} points. Estimation saved locally.`
          : `Jira update failed: ${jiraError} — estimation saved locally.`
        );
      } else if (soundEnabled && !isUncertain) {
        playSound('consensus');
      }

      // Auto-advance queue
      if (ticketQueue.length > 0 && queueIndex + 1 < ticketQueue.length) {
        await handleNextInQueue();
      } else {
        if (ticketQueue.length > 0) { setTicketQueue([]); setQueueIndex(0); }
        await loadSession();
      }
    } catch (e) {
      setError(e.message);
    } finally {
      setSavingToJira(false);
    }
  };

  const handleKick = async (participantId) => {
    try {
      await api.sessions.kick(sessionId, participantId);
      setParticipants(ps => ps.filter(p => p.id !== participantId));
    } catch (e) { setError(e.message); }
  };

  const handleToggleObserver = async (participantId, currentIsObserver) => {
    try {
      await api.sessions.setObserver(sessionId, participantId, !currentIsObserver);
      setParticipants(ps => ps.map(p => p.id === participantId ? { ...p, is_observer: !currentIsObserver } : p));
    } catch (e) { setError(e.message); }
  };

  const handleTransferFacilitator = async (p) => {
    if (!window.confirm(`Hand facilitator role to ${p.display_name}?`)) return;
    try {
      await api.sessions.transferFacilitator(sessionId, p.jira_id);
      await loadSession();
    } catch (e) {
      setError(e.message);
    }
  };

  const copyShareLink = (observer = false) => {
    const url = `${window.location.origin}/join/${sessionId}${observer ? '?observer=true' : ''}`;
    navigator.clipboard.writeText(url);
    if (observer) { setCopiedObserver(true); setTimeout(() => setCopiedObserver(false), 2000); }
    else { setCopied(true); setTimeout(() => setCopied(false), 2000); }
  };

  const voteDistribution = () => {
    const dist = {};
    votesThisRound.forEach(v => { dist[v.vote_value] = (dist[v.vote_value] || 0) + 1; });
    return dist;
  };

  const isConsensus = () => Object.keys(voteDistribution()).length === 1;

  if (loading) {
    return (
      <div className="page flex items-center justify-center" style={{ minHeight: '60vh' }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '3rem', animation: 'pulse 1.5s ease infinite' }}>🃏</div>
          <p className="text-muted mt-4">Loading session...</p>
        </div>
      </div>
    );
  }

  if (!session) {
    return (
      <div className="page flex items-center justify-center" style={{ minHeight: '60vh' }}>
        <div className="card" style={{ textAlign: 'center' }}>
          <p className="text-muted">{error || 'Session not found.'}</p>
          <button className="btn btn-primary mt-4" onClick={() => navigate('/')}>Go Home</button>
        </div>
      </div>
    );
  }

  const isVoting = currentRound?.status === 'voting' || currentRound?.status === 'revoting';
  const isRevealed = currentRound?.status === 'revealed';
  const myParticipant = participants.find(p => p.id === myParticipantId);
  const canVote = myParticipant && !myParticipant.is_observer;
  const queueActive = ticketQueue.length > 0;
  const queueRemaining = ticketQueue.length - queueIndex - 1;

  return (
    <div className="page page-wide">
      {/* Session Header */}
      <div className="flex items-center gap-4 mb-6 session-header">
        <div style={{ flex: 1, minWidth: 0 }}>
          <div className="flex items-center gap-3 mb-1 flex-wrap">
            <h1 style={{ fontSize: '1.4rem' }}>{session.name}</h1>
            <span className="badge badge-green">Live</span>
            {currentRound?.status === 'revoting' && <span className="badge badge-yellow">Re-vote</span>}
            {queueActive && (
              <span className="badge badge-blue">
                <List size={10} /> Queue: {queueIndex + 1}/{ticketQueue.length}
              </span>
            )}
          </div>
          <p className="text-sm text-muted">
            {session.teams?.name}
            {session.teams?.jira_project_key && <span className="font-mono"> · {session.teams.jira_project_key}</span>}
            {' · '}Facilitated by {session.facilitator_name}
          </p>
        </div>

        <div className="flex items-center gap-2 session-header-actions flex-wrap">
          <button className="btn btn-ghost btn-icon" onClick={() => setSoundEnabled(s => !s)}
            title={soundEnabled ? 'Mute sounds' : 'Enable sounds'}>
            {soundEnabled ? <Volume2 size={16} /> : <VolumeX size={16} />}
          </button>
          <button className="btn btn-secondary btn-sm" onClick={() => copyShareLink(false)}>
            {copied ? <><CheckCircle size={14} /> Copied!</> : <><Share2 size={14} /> Share</>}
          </button>
          {isFacilitator && (
            <button className="btn btn-primary btn-sm" onClick={() => setShowTicketPicker(true)}>
              <Plus size={14} /> Pick Ticket
            </button>
          )}
          {isFacilitator && session.status === 'active' && (
            <button className="btn btn-secondary btn-sm" style={{ color: 'var(--red)' }}
              onClick={async () => {
                if (!window.confirm('End this session?')) return;
                try { await api.sessions.end(sessionId); navigate('/'); }
                catch (e) { setError(e.message); }
              }}>
              <XCircle size={14} /> End Session
            </button>
          )}
        </div>
      </div>

      <div className="layout-session">
        {/* Left: Main area */}
        <div>
          {/* Queue progress bar */}
          {queueActive && (
            <div className="card card-sm mb-4" style={{ background: 'var(--blue-dim)', border: '1px solid var(--blue)' }}>
              <div className="flex items-center gap-3">
                <List size={14} style={{ color: 'var(--blue)', flexShrink: 0 }} />
                <div style={{ flex: 1 }}>
                  <div className="text-sm font-bold" style={{ color: 'var(--blue)' }}>
                    Ticket {queueIndex + 1} of {ticketQueue.length}
                    {queueRemaining > 0 && <span className="text-muted font-normal"> · {queueRemaining} remaining</span>}
                  </div>
                  <div style={{
                    height: 4, background: 'var(--border)', borderRadius: 2, marginTop: 4
                  }}>
                    <div style={{
                      height: '100%', borderRadius: 2, background: 'var(--blue)',
                      width: `${((queueIndex + 1) / ticketQueue.length) * 100}%`,
                      transition: 'width 0.4s ease'
                    }} />
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Ticket info */}
          {currentRound ? (
            <TicketInfo
              round={currentRound}
              isFacilitator={isFacilitator}
              onShowBaselines={() => setShowBaselines(b => !b)}
              showBaselines={showBaselines}
              onSkip={handleSkip}
            />
          ) : (
            <div className="card" style={{ textAlign: 'center', padding: '48px', marginBottom: 24 }}>
              <div style={{ fontSize: '2.5rem', marginBottom: 12 }}>🎯</div>
              {isFacilitator ? (
                <>
                  <p className="font-bold mb-2">Ready to estimate!</p>
                  <p className="text-muted text-sm mb-4">Pick a single ticket or build a queue.</p>
                  <button className="btn btn-primary" onClick={() => setShowTicketPicker(true)}>
                    <Plus size={14} /> Pick Ticket
                  </button>
                </>
              ) : (
                <>
                  <p className="text-muted">Waiting for the facilitator to pick a ticket...</p>
                  <div className="pulse mt-3 text-sm text-accent">● Live</div>
                </>
              )}
            </div>
          )}

          {/* Estimation guidance */}
          {currentRound && (
            <EstimationGuidancePanel
              roundNumber={currentRound.round_number}
              isRevealed={isRevealed}
            />
          )}

          {/* Baselines panel */}
          {showBaselines && (
            <div style={{ marginBottom: 24 }}>
              <BaselinePanel
                teamId={session.team_id}
                teamName={session.teams?.name}
                currentPoints={isRevealed ? (() => {
                  const dist = voteDistribution();
                  const top = Object.entries(dist).sort((a, b) => b[1] - a[1])[0];
                  return top ? parseInt(top[0]) || null : null;
                })() : null}
                onClose={() => setShowBaselines(false)}
                isFacilitator={isFacilitator}
                cloudName={user?.cloudName}
              />
            </div>
          )}

          {/* Voting cards */}
          {currentRound && isVoting && canVote && (
            <div style={{ marginBottom: 24 }}>
              <p className="text-sm text-muted mb-3 font-bold"
                style={{ textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                {currentRound.status === 'revoting' ? '🔄 Re-vote — ' : ''}Your Estimate
              </p>
              <div className="flex gap-3" style={{ flexWrap: 'wrap' }}>
                {CARD_VALUES.map(v => (
                  <div key={v}
                    className={`poker-card ${myVote === v ? 'selected' : ''}`}
                    onClick={() => handleVote(v)}>
                    {v}
                  </div>
                ))}
              </div>
              {myVote && (
                <div className="toast toast-success mt-3" style={{ width: 'fit-content' }}>
                  <CheckCircle size={14} /> You voted <strong>{myVote}</strong>
                </div>
              )}
            </div>
          )}

          {/* Observer note */}
          {currentRound && isVoting && myParticipant?.is_observer && (
            <div className="toast toast-info mb-4">
              <Eye size={14} /> You are observing this round
            </div>
          )}

          {/* Vote results */}
          {currentRound && isRevealed && (
            <VoteResults
              votes={votesThisRound}
              allVotes={allRoundVotes}
              participants={participants}
              isConsensus={isConsensus()}
              isFacilitator={isFacilitator}
              myParticipantId={myParticipantId}
              myReaction={myReaction}
              onReact={handleReact}
              onRevote={handleRevote}
              onComplete={() => setShowCompleteModal(true)}
              showHistory={showVoteHistory}
              onToggleHistory={() => setShowVoteHistory(h => !h)}
              currentRoundNumber={currentRound.round_number}
            />
          )}

          {/* Facilitator voting controls */}
          {currentRound && isVoting && isFacilitator && (
            <div className="flex gap-3 mt-4 flex-wrap">
              <div className="text-sm text-muted flex items-center gap-2">
                <span style={{ color: allVoted ? 'var(--green)' : 'inherit' }}>
                  {votedIds.size}/{voterIds.size} voted
                </span>
                {allVoted && <span className="badge badge-green">All in!</span>}
              </div>
              <button className="btn btn-primary btn-sm" style={{ marginLeft: 'auto' }} onClick={handleReveal}>
                Reveal Cards
              </button>
            </div>
          )}

          {/* Completed rounds log */}
          <CompletedRounds
            rounds={rounds.filter(r => r.status === 'complete' || r.status === 'skipped')}
            participants={participants}
            sessionId={sessionId}
          />
        </div>

        {/* Right: Sidebar */}
        <div className="sidebar-sticky">
          <div className="card">
            <div className="flex items-center justify-between"
              style={{ cursor: 'pointer', marginBottom: showParticipants ? 16 : 0 }}
              onClick={() => setShowParticipants(s => !s)}>
              <div className="flex items-center gap-2">
                <Users size={14} />
                <span className="font-bold text-sm">
                  Participants ({participants.filter(p => p.is_active).length})
                </span>
              </div>
              {showParticipants ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
            </div>

            {showParticipants && (
              <div className="flex-col">
                {participants.filter(p => p.is_active).map(p => {
                  const hasVoted = votedIds.has(p.id);
                  const vote = isRevealed ? votesThisRound.find(v => v.participant_id === p.id)?.vote_value : null;

                  return (
                    <div key={p.id} className="participant-item">
                      <div className="participant-avatar">
                        {p.display_name.charAt(0).toUpperCase()}
                      </div>
                      <div style={{ flex: 1 }}>
                        <div className="text-sm" style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>{p.display_name}</div>
                        {p.is_facilitator && <div className="text-xs" style={{ color: 'var(--accent)' }}>Facilitator</div>}
                        {p.is_observer && <div className="text-xs text-muted flex items-center gap-1"><Eye size={10} /> Observer</div>}
                      </div>

                      {latestReactionByParticipant[p.id] && (
                        <span style={{ fontSize: 16, lineHeight: 1 }}>{latestReactionByParticipant[p.id]}</span>
                      )}
                      {vote && <span className="badge badge-purple font-mono">{vote}</span>}
                      {!vote && !p.is_observer && isVoting && (
                        <div className={`vote-dot ${hasVoted ? 'voted' : 'pending'}`} />
                      )}

                      {(isFacilitator || p.id === myParticipantId) && (
                        <button className="btn btn-ghost btn-icon btn-sm"
                          onClick={() => handleToggleObserver(p.id, p.is_observer)}
                          title={p.is_observer ? 'Switch to Voter' : 'Switch to Observer'}
                          style={{ color: 'var(--text-muted)' }}>
                          {p.is_observer ? <Users size={12} /> : <Eye size={12} />}
                        </button>
                      )}

                      {isFacilitator && p.jira_id && p.id !== myParticipantId && (
                        <button
                          className="btn btn-ghost btn-sm"
                          style={{ fontSize: 10, color: 'var(--blue)', flexShrink: 0 }}
                          onClick={() => handleTransferFacilitator(p)}
                          title="Make facilitator"
                        >
                          Make facilitator
                        </button>
                      )}

                      {isFacilitator && !p.is_facilitator && (
                        <button className="btn btn-ghost btn-icon btn-sm"
                          onClick={() => handleKick(p.id)}
                          title="Remove participant"
                          style={{ color: 'var(--text-muted)' }}>
                          <UserX size={12} />
                        </button>
                      )}
                    </div>
                  );
                })}

                {isFacilitator && (
                  <>
                    <div className="divider" />
                    <button className="btn btn-ghost btn-sm w-full" onClick={() => copyShareLink(false)}>
                      <Copy size={12} /> {copied ? 'Copied!' : 'Copy invite link'}
                    </button>
                    <button className="btn btn-ghost btn-sm w-full mt-1"
                      style={{ color: 'var(--blue)' }}
                      onClick={() => copyShareLink(true)}>
                      <Link2 size={12} /> {copiedObserver ? 'Copied!' : 'Copy observer link'}
                    </button>
                  </>
                )}
              </div>
            )}
          </div>

          {/* FEAT-03: Live Queue Management Panel — facilitator only, queue must be active */}
          {isFacilitator && queueActive && (
            <div className="card queue-panel">
              <div className="queue-panel-title">
                <List size={12} /> Queue ({ticketQueue.length - queueIndex} remaining)
              </div>
              {ticketQueue.slice(queueIndex).map((ticket, relIdx) => {
                const absIdx = relIdx + queueIndex;
                const isCurrent = absIdx === queueIndex;
                return (
                  <div
                    key={ticket.id || ticket.jira_ticket_key}
                    className={`queue-item${isCurrent ? ' queue-item--current' : ''}`}
                    onClick={() => !isCurrent && handleJumpToQueueItem(absIdx)}
                  >
                    <span className="queue-item-num">{relIdx + 1}</span>
                    <span className="queue-item-key">{ticket.jira_ticket_key}</span>
                    {isCurrent && <span className="queue-item-current-label">current</span>}
                    {!isCurrent && (
                      <div className="queue-item-actions">
                        <button
                          className="btn btn-ghost btn-icon btn-sm"
                          title="Move up"
                          onClick={e => { e.stopPropagation(); handleMoveInQueue(absIdx, -1); }}
                        >↑</button>
                        <button
                          className="btn btn-ghost btn-icon btn-sm"
                          title="Move down"
                          onClick={e => { e.stopPropagation(); handleMoveInQueue(absIdx, +1); }}
                        >↓</button>
                        <button
                          className="btn btn-ghost btn-icon btn-sm"
                          title="Remove from queue"
                          style={{ color: 'var(--text-muted)' }}
                          onClick={e => { e.stopPropagation(); handleRemoveFromQueue(absIdx); }}
                        >✕</button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Modals */}
      {showTicketPicker && (
        <TicketPicker
          team={session.teams}
          onSelect={handleSelectTicket}
          onQueue={handleQueueTickets}
          onClose={() => setShowTicketPicker(false)}
        />
      )}

      {showCompleteModal && (
        <CompleteModal
          round={currentRound}
          votes={votesThisRound}
          finalPoints={finalPoints}
          setFinalPoints={setFinalPoints}
          isUncertain={isUncertain}
          setIsUncertain={setIsUncertain}
          isBaseline={isBaseline}
          setIsBaseline={setIsBaseline}
          baselineNotes={baselineNotes}
          setBaselineNotes={setBaselineNotes}
          comment={comment}
          setComment={setComment}
          saving={savingToJira}
          onConfirm={handleComplete}
          onClose={() => setShowCompleteModal(false)}
          queueRemaining={queueActive ? queueRemaining : 0}
        />
      )}

      {/* BUG-05: ephemeral reaction overlay */}
      {reactions.length > 0 && (
        <div className="reaction-overlay">
          {reactions.map(r => (
            <div key={r.id} className="reaction-bubble">
              <span className="reaction-emoji">{r.emoji}</span>
              <span className="text-muted">{r.name}</span>
            </div>
          ))}
        </div>
      )}

      {error && (
        <div style={{ position: 'fixed', bottom: 24, right: 24, zIndex: 300 }}>
          <div className="toast toast-error">
            <AlertTriangle size={14} /> {error}
            <button className="btn btn-ghost btn-sm" onClick={() => setError('')}>✕</button>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Sub-components ────────────────────────────────────────────

function TicketInfo({ round, isFacilitator, onShowBaselines, showBaselines, onSkip }) {
  return (
    <div className="card mb-6">
      <div className="flex items-start gap-4">
        <div style={{ flex: 1, minWidth: 0 }}>
          <div className="flex items-center gap-2 mb-2 flex-wrap">
            <span className="badge badge-muted font-mono">{round.jira_ticket_key}</span>
            {round.jira_ticket_type && <span className="badge badge-blue">{round.jira_ticket_type}</span>}
            {round.jira_ticket_priority && <span className="text-xs text-muted">{round.jira_ticket_priority}</span>}
            {round.round_number > 1 && <span className="badge badge-yellow">Round {round.round_number}</span>}
          </div>
          <h2 style={{ fontSize: '1.2rem', marginBottom: 8 }}>{round.jira_ticket_summary}</h2>
          {round.jira_ticket_url && (
            <a href={round.jira_ticket_url} target="_blank" rel="noopener noreferrer"
              className="btn btn-ghost btn-sm" style={{ padding: '4px 0', color: 'var(--accent)' }}>
              Open in Jira <ExternalLink size={12} />
            </a>
          )}
        </div>

        <div className="flex items-center gap-2" style={{ flexShrink: 0 }}>
          <button
            className={`btn btn-sm ${showBaselines ? 'btn-secondary' : 'btn-ghost'}`}
            style={{ color: showBaselines ? 'var(--yellow)' : undefined }}
            onClick={onShowBaselines} title="Show baseline references">
            <Bookmark size={14} /> Baselines
          </button>
          {isFacilitator && (
            <button className="btn btn-ghost btn-sm" style={{ color: 'var(--text-muted)' }}
              onClick={onSkip} title="Skip this ticket">
              <SkipForward size={14} /> Skip
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function VoteResults({
  votes, allVotes, participants, isConsensus, isFacilitator,
  myParticipantId, myReaction, onReact,
  onRevote, onComplete, showHistory, onToggleHistory, currentRoundNumber
}) {
  const dist = {};
  votes.forEach(v => { dist[v.vote_value] = (dist[v.vote_value] || 0) + 1; });
  const maxCount = Math.max(...Object.values(dist), 1);
  const sorted = Object.entries(dist).sort((a, b) => b[1] - a[1]);

  // Group allVotes by round_number for history
  const votesByRound = {};
  allVotes.forEach(v => {
    if (!votesByRound[v.vote_number]) votesByRound[v.vote_number] = [];
    votesByRound[v.vote_number].push(v);
  });
  const hasHistory = currentRoundNumber > 1;

  return (
    <div className="card mb-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <h3 style={{ fontSize: '1rem' }}>Results</h3>
          {hasHistory && (
            <button className="btn btn-ghost btn-sm" style={{ color: 'var(--text-muted)', fontSize: 11 }}
              onClick={onToggleHistory}>
              <History size={12} /> {showHistory ? 'Hide' : 'Show'} history
            </button>
          )}
        </div>
        {isConsensus
          ? <span className="badge badge-green">✓ Consensus!</span>
          : <span className="badge badge-yellow">No consensus</span>}
      </div>

      {/* Vote history (previous rounds) */}
      {showHistory && hasHistory && (
        <div style={{
          background: 'var(--bg-elevated)', border: '1px solid var(--border)',
          borderRadius: 'var(--radius-sm)', padding: 12, marginBottom: 16
        }}>
          <p className="text-xs font-bold text-muted mb-2" style={{ textTransform: 'uppercase', letterSpacing: '0.06em' }}>
            Vote History
          </p>
          {Object.entries(votesByRound)
            .sort((a, b) => parseInt(a[0]) - parseInt(b[0]))
            .map(([roundNum, roundVotes]) => (
              <div key={roundNum} style={{ marginBottom: 8 }}>
                <p className="text-xs text-muted mb-1">Round {roundNum}{parseInt(roundNum) === currentRoundNumber ? ' (current)' : ''}</p>
                <div className="flex gap-2 flex-wrap">
                  {roundVotes.map(v => {
                    const p = participants.find(p => p.id === v.participant_id);
                    return (
                      <div key={v.id} className="flex items-center gap-1" style={{
                        background: 'var(--bg-card)', border: '1px solid var(--border)',
                        borderRadius: 'var(--radius-sm)', padding: '2px 8px', fontSize: 11
                      }}>
                        <span className="text-muted">{p?.display_name || '?'}</span>
                        <span className="badge badge-purple font-mono" style={{ padding: '0px 4px', fontSize: 10 }}>{v.vote_value}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
        </div>
      )}

      {/* Current round distribution */}
      <div style={{ marginBottom: 16 }}>
        {sorted.map(([val, count]) => (
          <div key={val} className="vote-bar-row">
            <span className="vote-bar-label">{val}</span>
            <div className="vote-bar-track">
              <div
                className={`vote-bar-fill ${count === maxCount && isConsensus ? 'consensus' : ''}`}
                style={{ width: `${(count / maxCount) * 100}%` }}
              />
            </div>
            <span className="vote-bar-count">{count} vote{count !== 1 ? 's' : ''}</span>
          </div>
        ))}
      </div>

      {/* Who voted what */}
      <div className="flex gap-2 flex-wrap" style={{ marginBottom: 16 }}>
        {votes.map(v => {
          const p = participants.find(p => p.id === v.participant_id);
          return (
            <div key={v.id} className="flex items-center gap-1" style={{
              background: 'var(--bg-elevated)', border: '1px solid var(--border)',
              borderRadius: 'var(--radius-sm)', padding: '4px 10px'
            }}>
              <span className="text-xs text-muted">{p?.display_name || 'Unknown'}</span>
              <span className="badge badge-purple font-mono" style={{ padding: '1px 6px' }}>{v.vote_value}</span>
            </div>
          );
        })}
      </div>

      {/* Emoji reactions */}
      <div className="flex items-center gap-2 mb-4">
        <span className="text-xs text-muted">React:</span>
        {REACTIONS.map(r => (
          <button
            key={r.emoji}
            className="btn btn-sm"
            style={{
              padding: '4px 10px', fontSize: 16,
              background: myReaction === r.emoji ? 'var(--accent-dim)' : 'var(--bg-elevated)',
              border: `1px solid ${myReaction === r.emoji ? 'var(--accent)' : 'var(--border)'}`,
              transition: 'all 0.15s'
            }}
            onClick={() => onReact(myReaction === r.emoji ? null : r.emoji)}
            title={r.label}
          >
            {r.emoji}
          </button>
        ))}
      </div>

      {isFacilitator && (
        <div className="flex gap-3 vote-result-actions">
          <button className="btn btn-secondary btn-sm" onClick={onRevote}>
            <RotateCcw size={14} /> Re-vote
          </button>
          <button className="btn btn-primary vote-complete-btn" style={{ marginLeft: 'auto' }} onClick={onComplete}>
            <Zap size={14} /> Set Points & Save to Jira
          </button>
        </div>
      )}
    </div>
  );
}

function CompleteModal({
  round, votes, finalPoints, setFinalPoints,
  isUncertain, setIsUncertain, isBaseline, setIsBaseline,
  baselineNotes, setBaselineNotes,
  comment, setComment,
  saving, onConfirm, onClose, queueRemaining
}) {
  const topVote = (() => {
    const dist = {};
    votes.forEach(v => { dist[v.vote_value] = (dist[v.vote_value] || 0) + 1; });
    return Object.entries(dist).sort((a, b) => b[1] - a[1])[0]?.[0];
  })();

  useEffect(() => {
    if (topVote && !isNaN(parseInt(topVote))) setFinalPoints(topVote);
  }, []);

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal">
        <div className="modal-header">
          <h3>Finalise Estimate</h3>
          <button className="btn btn-ghost btn-icon" onClick={onClose}>✕</button>
        </div>

        <p className="text-sm text-muted mb-4">
          <strong>{round.jira_ticket_key}</strong> — {round.jira_ticket_summary}
        </p>

        {queueRemaining > 0 && (
          <div className="toast toast-info mb-4" style={{ fontSize: 12 }}>
            <List size={12} /> {queueRemaining} more ticket{queueRemaining !== 1 ? 's' : ''} in the queue after this.
          </div>
        )}

        <div className="input-group">
          <label className="input-label">Final Story Points</label>
          <div className="flex gap-2 flex-wrap mb-2">
            {CARD_VALUES.filter(v => !isNaN(parseInt(v))).map(v => (
              <button key={v}
                className={`btn btn-sm ${finalPoints === v ? 'btn-primary' : 'btn-secondary'}`}
                onClick={() => setFinalPoints(v)}>
                {v}
              </button>
            ))}
          </div>
          <input className="input" type="text" value={finalPoints}
            onChange={e => setFinalPoints(e.target.value)}
            placeholder="Or enter custom value..." />
        </div>

        <div className="divider" />

        <div className="flex-col gap-3 mb-4">
          <ToggleOption
            icon={<AlertTriangle size={14} />}
            label="Mark as Uncertain"
            desc="Adds 're-estimate' label to Jira for retro review"
            active={isUncertain} color="var(--yellow)"
            onClick={() => setIsUncertain(u => !u)}
          />
          <ToggleOption
            icon={<Bookmark size={14} />}
            label="Save as Baseline"
            desc="Use as a reference for future estimation"
            active={isBaseline} color="var(--accent)"
            onClick={() => setIsBaseline(b => !b)}
          />
        </div>

        {isBaseline && (
          <div className="input-group">
            <label className="input-label">Baseline Notes (optional)</label>
            <input className="input" value={baselineNotes}
              onChange={e => setBaselineNotes(e.target.value)}
              placeholder="e.g. 'Classic 5-pointer: auth flow'" />
          </div>
        )}

        <div className="input-group">
          <label className="input-label">Add a comment (optional)</label>
          <textarea
            className="input"
            style={{ minHeight: 72, resize: 'vertical' }}
            value={comment}
            onChange={e => setComment(e.target.value)}
            placeholder="e.g. Needs split into subtasks before next sprint..."
          />
        </div>

        <div className="flex gap-3">
          <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary w-full" onClick={onConfirm}
            disabled={saving || !finalPoints}>
            {saving ? 'Saving to Jira...' : (
              queueRemaining > 0
                ? <><CheckCircle size={14} /> Save & Next Ticket</>
                : <><CheckCircle size={14} /> Confirm & Update Jira</>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

function ToggleOption({ icon, label, desc, active, color, onClick }) {
  return (
    <div className="flex items-center gap-3"
      style={{
        padding: '10px 14px',
        background: active ? `color-mix(in srgb, ${color} 15%, transparent)` : 'var(--bg-elevated)',
        border: `1px solid ${active ? color : 'var(--border)'}`,
        borderRadius: 'var(--radius-sm)', cursor: 'pointer', transition: 'all 0.15s'
      }}
      onClick={onClick}>
      <span style={{ color: active ? color : 'var(--text-muted)' }}>{icon}</span>
      <div style={{ flex: 1 }}>
        <div className="text-sm font-bold" style={{ color: active ? color : 'var(--text-primary)' }}>{label}</div>
        <div className="text-xs text-muted">{desc}</div>
      </div>
      <div style={{
        width: 18, height: 18, borderRadius: '50%',
        background: active ? color : 'var(--border)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: 10, color: active ? 'white' : 'transparent', transition: 'all 0.15s'
      }}>✓</div>
    </div>
  );
}

function CompletedRounds({ rounds, participants, sessionId }) {
  const [expandedId, setExpandedId] = useState(null);
  const [expandedVotes, setExpandedVotes] = useState({});

  const handleToggle = async (round) => {
    if (round.status === 'skipped') return;
    const next = expandedId === round.id ? null : round.id;
    setExpandedId(next);
    if (next && !expandedVotes[next]) {
      setExpandedVotes(prev => ({ ...prev, [next]: { loading: true, votes: null } }));
      try {
        const data = await api.sessions.allVotes(sessionId, next);
        setExpandedVotes(prev => ({ ...prev, [next]: { loading: false, votes: data } }));
      } catch {
        setExpandedVotes(prev => ({ ...prev, [next]: { loading: false, votes: [] } }));
      }
    }
  };

  if (rounds.length === 0) return null;
  return (
    <div style={{ marginTop: 40 }}>
      <h3 style={{ fontSize: '1rem', marginBottom: 12 }}>Completed This Session</h3>
      <div className="flex-col gap-2">
        {rounds.map(r => {
          const isOpen = expandedId === r.id;
          const canExpand = r.status !== 'skipped';
          const cached = expandedVotes[r.id];
          return (
            <div key={r.id} className="card card-sm" style={{ padding: '10px 16px' }}>
              <div
                className="flex items-center gap-3"
                style={{ cursor: canExpand ? 'pointer' : 'default' }}
                onClick={() => handleToggle(r)}
              >
                {r.jira_ticket_url ? (
                  <a
                    href={r.jira_ticket_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={e => e.stopPropagation()}
                    className="badge badge-muted font-mono"
                    style={{ textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 4 }}
                  >
                    {r.jira_ticket_key} <ExternalLink size={10} />
                  </a>
                ) : (
                  <span className="badge badge-muted font-mono">{r.jira_ticket_key}</span>
                )}
                <span className="text-sm truncate flex-1">{r.jira_ticket_summary}</span>
                {r.status === 'skipped'
                  ? <span className="badge badge-muted">⏭ skipped</span>
                  : <span className="badge badge-purple">{r.final_points != null ? `${r.final_points} pts` : "—"}</span>
                }
                {r.is_uncertain && <span className="badge badge-yellow">⚠ uncertain</span>}
                {r.is_baseline && <span className="badge badge-green">⚓ baseline</span>}
                {canExpand && (
                  <span style={{ color: 'var(--text-muted)', fontSize: 11, flexShrink: 0 }}>
                    {isOpen ? '▲' : '▼'}
                  </span>
                )}
              </div>

              {isOpen && (
                <div style={{
                  marginTop: 10, paddingTop: 10,
                  borderTop: '1px solid var(--border)'
                }}>
                  {cached?.loading && (
                    <p className="text-xs text-muted">Loading votes…</p>
                  )}
                  {cached && !cached.loading && cached.votes !== null && (() => {
                    const byRound = {};
                    cached.votes.forEach(v => {
                      if (!byRound[v.vote_number]) byRound[v.vote_number] = [];
                      byRound[v.vote_number].push(v);
                    });
                    return Object.entries(byRound)
                      .sort((a, b) => parseInt(a[0]) - parseInt(b[0]))
                      .map(([roundNum, votes]) => (
                        <div key={roundNum} style={{ marginBottom: 8 }}>
                          <p className="text-xs text-muted font-bold mb-1">Round {roundNum}</p>
                          <div className="flex gap-2 flex-wrap">
                            {votes.map(v => {
                              const p = participants.find(p => p.id === v.participant_id);
                              return (
                                <div key={v.id} style={{
                                  background: 'var(--bg-elevated)',
                                  border: '1px solid var(--border)',
                                  borderRadius: 'var(--radius-sm)',
                                  padding: '2px 8px',
                                  fontSize: 11,
                                  display: 'flex', alignItems: 'center', gap: 4
                                }}>
                                  <span className="text-muted">{p?.display_name || '?'}</span>
                                  <span className="badge badge-purple font-mono" style={{ padding: '0 4px', fontSize: 10 }}>{v.vote_value}</span>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      ));
                  })()}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
